import {gql} from '@apollo/client';
import {css} from 'aphrodite';
import {DocumentNode} from 'graphql';
import React, {useCallback, useMemo, useState} from 'react';

import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {StudyType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {
  getNewConditionCount,
  getNewConditionReplicateCount,
  UpdateProcedureRunParams,
} from 'procedures/network/ProcedureRunDAO';
import getProcedureUIString, {ProceduresStringKeys, StringCase} from 'procedures/ProceduresStringKeys';
import {
  getFlowchartVersionIdForNavigation,
  getPowAgnosticProcedureFromFlowchartVersion,
  getPowAgnosticTemplateFlowchartVersionFromProcedureRun,
} from 'procedures/utils/powAgnosticFlowchartHelpers';
import useTimingEstimate from 'studies/progress/useTimingEstimate';
import {ProcedureRunTab} from 'studies/redux/ProcedureRunPlanUIReduxActions';
import {refreshProcedureRunDesign, refreshProcedureRunDetailsView} from 'studies/sidepanel/TrayTaskUtils';
import {
  createDefaultConditionGroups,
  useProcedureRunUpdateParam,
} from 'studies/typed/shared/procedure-runs/design-state/useExperimentDesignState';
import {shouldShowFlowchartProcedureRunDesign} from 'studies/typed/shared/procedure-runs/design-tabs/ProcedureRunDesignUtils';
import {
  getProcedureRunUIStringForStudyType,
  getProcedureTemplateStringForStudyType,
} from 'studies/TypedStudyUtils';
import getTestProps from 'test/lib/cypress/getTestProps';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import FlowchartSidebar from 'ui-library/reactflow/sidepanels/FlowchartSidebar';

import {StudyInaccessibleContentBanner} from '../../../utils/ui/StudyQueryBanners';
import {buildConditionReplicateFlowchartInput} from '../../flowcharts/design/buildConditionReplicateFlowchartInput';
import {isActiveLayerConnected} from '../../flowcharts/design/procedureRunDesignFlowchartConnectionUtils';
import ProcedureRunDesignFlowchartEditor from '../../flowcharts/design/ProcedureRunDesignFlowchartEditor';
import {
  buildNodeIdToPmeiIdMap,
  filterEdgeSpecsTouchingRemovedNodes,
} from '../../flowcharts/design/ProcedureRunDesignFlowchartLayoutUtils';
import {parseProcedureRunDesignFlowchartNodeId} from '../../flowcharts/design/ProcedureRunDesignFlowchartNodeIdUtils';
import {useProcedureRunDesignEdgeEdits} from '../../flowcharts/design/useProcedureRunDesignEdgeEdits';
import {useProcedureRunDesignFlowchartEdgeSpecs} from '../../flowcharts/design/useProcedureRunDesignFlowchartEdgeSpecs';
import {useProcedureRunDesignNodeRemovalEdits} from '../../flowcharts/design/useProcedureRunDesignNodeRemovalEdits';
import {onOpenAfterAddConditions} from '../conditions/ConditionCreationTrayTask';
import {buildConditionsWithAddedGroups} from '../conditions/conditionUtils';
import {
  EditNumConditionGroupsButtons,
  runConditionGroupsButtonVariant,
} from '../conditions/EditNumConditionGroupsButtons';
import getStudyDesignFromProcedureRun, {
  getMethodExecutionInstanceInfoForPlacements,
  STUDY_DESIGN_FROM_PROCEDURE_RUN_FRAGMENT,
} from '../design-state/getStudyDesignFromProcedureRun';
import {
  areProcedureRunInputsEvaluatedValuesValid,
  isNumConditionReplicatesValid,
  validateConditions,
} from '../design-state/procedureRunStudyValidationUtils';
import {useStudyDesignState} from '../design-state/useStudyDesignState';
import {getActiveAndDownstreamMethodIds} from '../execution-status/activeMethodUtils';
import {useProcedureRunMethodStatuses} from '../execution-status/useProcedureRunMethodStatuses';
import {StudyOverviewContext} from '../overview/StudyOverviewContext';
import {onAddConditions, onUpdateProcedureRun} from '../procedure-selection/procedureRunUpdateHandlers';
import {
  MultiProcedureRunStudyDesignTabContentsQuery,
  MultiProcedureRunStudyDesignTabContentsQueryVariables,
  MultiProcedureRunStudyDesignTabContentsQuery_procedureRun_ProcedureRunNode as ProcedureRunNode,
} from './__generated/MultiProcedureRunStudyDesignTabContentsQuery';
import ProcedureRunDesignEditorLeftSidepanel from './ProcedureRunDesignEditorLeftSidepanel';
import ProcedureRunDesignEditorRightSidepanel from './ProcedureRunDesignEditorRightSidepanel';
import StudyDesignTabSavingButtons, {StudyDesignTabEditState} from './StudyDesignTabSavingButtons';
import {
  ContextPanel,
  PARAMETER_VALUES_TAB_NAME,
  ParameterValuesTabContents,
  SETTINGS_TAB_NAME,
  SettingsTabContents,
  studyDesignTabStyles,
} from './StudyDesignTabSharedComponents';
import UpdateRunDesignConfirmationModal from './UpdateRunDesignConfirmationModal';

export const MULTI_PROCEDURE_RUN_STUDY_DESIGN_TAB_CONTENTS_QUERY: DocumentNode = gql`
  query MultiProcedureRunStudyDesignTabContentsQuery($procedureRunId: StrictString!)
  @skipAudit
  @engComponent(name: STUDIES) {
    procedureRun(id: $procedureRunId) {
      ...GetStudyDesignFromProcedureRunGraphQL
      ... on ProcedureRunNode {
        id
        procedureType
        defaultDataEntryMode
        flowchartIsExpanded
        templateProcedureFlowchartVersion {
          ...ExperimentalInputsFlowchartGraphQL
          ... on ProcedureFlowchartVersionNode {
            id
            largestTableTypeSizes {
              tableKind
              size
            }
            procedureFlowchart {
              id
              parent {
                id
              }
            }
          }
        }
        procedureFlowchartConfigVersionProxy {
          ... on ProcedureFlowchartConfigVersionProxyNode {
            id
            largestTableTypeSizes {
              tableKind
              size
            }
            procedure {
              id
            }
          }
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
  ${getFragmentWrapper('ProcedureFlowchartVersion').fragment}
  ${STUDY_DESIGN_FROM_PROCEDURE_RUN_FRAGMENT}
`;

export const UNSAVED_NEW_CONDITIONS_BANNER_TEXT = 'Set factors for new conditions before updating run plan';
export const NEW_WORKSHEETS_BANNER_TEXT = 'New worksheets will be created for new conditions.';

function ProcedureRunDesignTabContents(props: {
  procedureRun: ProcedureRunNode;
  studyType: StudyType;
  studyId: string;
  setCurrentProcedureRunTab: (tab: ProcedureRunTab) => void;
}): JSX.Element {
  const {isProcedureRunDescriptionInEditMode, setIsProcedureRunDesignInEditMode} =
    StudyOverviewContext.useContext();
  const {procedureRun} = props;
  const [isUpdateRunDesignModalOpen, setIsUpdateRunDesignModalOpen] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const isFlowchartProcedureRunDesignEnabled = shouldShowFlowchartProcedureRunDesign();
  const isSplitPoolEnabled = BenchlingConfig.STUDIES_ENABLE_SPLIT_POOL;

  const {
    experimentalInputs: initialExperimentalInputs,
    templateFlowchart,
    initialPersistedConditionGroups,
  } = useMemo(
    () =>
      getStudyDesignFromProcedureRun(procedureRun, {
        // Split-pool gates sparse per-method condition groups. Flowchart UI can render without
        // split-pool, but users still only create full bowling-lane conditions until it is on.
        requireBowlingLaneCoverage: !isSplitPoolEnabled,
      }),
    [procedureRun, isSplitPoolEnabled]
  );

  const flowchartVersion = getPowAgnosticTemplateFlowchartVersionFromProcedureRun(procedureRun);
  assert(!isInaccessible(flowchartVersion), 'Expected flowchart version to be accessible');
  const procedureId = getPowAgnosticProcedureFromFlowchartVersion(flowchartVersion).id;

  const {
    selectedMethodId,
    setSelectedMethodId,
    editState,
    setEditState,
    selectedDesignTabIndex,
    setSelectedDesignTabIndex,
    sidepanelName,
    setSidepanelName,
    conditions,
    setConditions,
    experimentalInputs,
    setExperimentalInputs,
    resetConditionsAndInputs,
    context,
    methodExecutionLimitState,
  } = useStudyDesignState({
    isPlan: false,
    initialExperimentalInputs,
    templateFlowchart,
    initialPersistedConditions: initialPersistedConditionGroups,
    createDefaultConditions: createDefaultConditionGroups,
    procedureType: procedureRun.procedureType,
    flowchartVersion,
    dataEntryMode: procedureRun.defaultDataEntryMode,
    procedureId,
    // Default to no method selected for flowchart procedure run design
    selectFirstMethodInitially: !isFlowchartProcedureRunDesignEnabled,
  });

  const updateEditState = useCallback(
    (nextEditState: StudyDesignTabEditState) => {
      setEditState(nextEditState);
      // Include SAVING so the header keeps the description unmounted until save completes.
      setIsProcedureRunDesignInEditMode(nextEditState !== StudyDesignTabEditState.SAVED);
    },
    [setEditState, setIsProcedureRunDesignInEditMode]
  );

  const isEmptyFlowchart = templateFlowchart.nodes.length === 0;

  // Each projection is gated by its own flag: the per-cell status map is used when flowchart
  // procedure run design is enabled, and the active method only when split-pool is also enabled.
  const {statusByNodeId, activeMethod, doesActiveMethodHaveDownstreamMethods} = useProcedureRunMethodStatuses(
    {
      procedureRun,
      methods: templateFlowchart.nodes,
      conditions,
      isFlowchartProcedureRunDesignEnabled,
      isSplitPoolEnabled,
    }
  );

  const edgeSpecs = useProcedureRunDesignFlowchartEdgeSpecs({
    isFlowchartProcedureRunDesignEnabled,
    conditions,
    methods: templateFlowchart.nodes,
    procedureRun,
    activeMethodId: activeMethod?.id ?? null,
    procedureEdges: templateFlowchart.edges,
  });
  const {drawnEdgeSpecs, removedEdgeIds, addDrawnEdge, removeEdge, resetEdits, applyEdits} =
    useProcedureRunDesignEdgeEdits(conditions);
  const {removedNodeIds, updateRemovedNodeIds, resetNodeRemovals} = useProcedureRunDesignNodeRemovalEdits(
    conditions,
    templateFlowchart.nodes
  );

  // Derived edges with in-session edits applied (drawn added, removed dropped): the source of truth for
  // both rendering and the save payload.
  const allEdgeSpecs = useMemo(() => applyEdits(edgeSpecs), [applyEdits, edgeSpecs]);
  const nodeIdToPmeiIdMap = useMemo(
    () => buildNodeIdToPmeiIdMap(getMethodExecutionInstanceInfoForPlacements(procedureRun), conditions),
    [procedureRun, conditions]
  );
  const onRemoveDraftConditionReplicate = useCallback(
    (conditionIndex: number, _replicateIndex: number): void => {
      const condition = conditions[conditionIndex];
      if (condition == null) {
        return;
      }

      if (condition.numReplicates > 1) {
        setConditions(
          conditions.map((existingCondition, index) =>
            index === conditionIndex
              ? {...existingCondition, numReplicates: existingCondition.numReplicates - 1}
              : existingCondition
          ),
          conditions.map((_, index) => index)
        );
        return;
      }

      setConditions(
        conditions.filter((_, index) => index !== conditionIndex),
        conditions
          .map((_, index) => (index === conditionIndex ? null : index))
          .filter((index) => index != null)
      );
    },
    [conditions, setConditions]
  );
  const methodExecutionInstanceIdsToRemove = useMemo(
    () =>
      [...removedNodeIds]
        .map((nodeId) => nodeIdToPmeiIdMap.get(nodeId))
        .filter((pmeiId): pmeiId is string => pmeiId != null),
    [removedNodeIds, nodeIdToPmeiIdMap]
  );
  const removedConditionReplicateCount = useMemo(() => {
    const conditionReplicateKeys = new Set<string>();
    for (const nodeId of removedNodeIds) {
      const {conditionIndex, replicateIndex} = parseProcedureRunDesignFlowchartNodeId(nodeId);
      conditionReplicateKeys.add(`${conditionIndex}-${replicateIndex}`);
    }
    return conditionReplicateKeys.size;
  }, [removedNodeIds]);
  const conditionReplicateFlowchart = useMemo(
    () =>
      isSplitPoolEnabled
        ? buildConditionReplicateFlowchartInput({
            conditions,
            edgeSpecs: filterEdgeSpecsTouchingRemovedNodes(allEdgeSpecs, removedNodeIds),
          })
        : undefined,
    [isSplitPoolEnabled, conditions, allEdgeSpecs, removedNodeIds]
  );

  const newConditionCoveredMethodIds = useMemo(() => {
    if (!isSplitPoolEnabled || activeMethod == null) {
      return undefined;
    }
    return [...getActiveAndDownstreamMethodIds(templateFlowchart.nodes, activeMethod.id)];
  }, [isSplitPoolEnabled, activeMethod, templateFlowchart.nodes]);
  // If active method is null and thus newConditionCoveredMethodIds is null, downstream unit op count
  // doesn't get used in the confirmation modal text.
  const downstreamUnitOperationCount =
    newConditionCoveredMethodIds != null ? newConditionCoveredMethodIds.length - 1 : null;

  // The flowchart add-replicate button appends a single draft condition-replicate lane covering the
  // active + downstream methods, reusing the same add path as the left-panel buttons.
  const onAddConditionReplicate = useCallback(() => {
    const {conditions: nextConditions, prevIndexes} = buildConditionsWithAddedGroups(conditions, {
      numConditions: 1,
      numReplicates: 1,
      procedureType: procedureRun.procedureType,
      coveredMethodIds: newConditionCoveredMethodIds,
    });
    setConditions(nextConditions, prevIndexes);
  }, [conditions, procedureRun.procedureType, newConditionCoveredMethodIds, setConditions]);

  const isEditDisabledForSplitPool = isSplitPoolEnabled && activeMethod == null;
  const isEditDisabledForDescription = isProcedureRunDescriptionInEditMode;
  const isEditDisabled = isEditDisabledForSplitPool || isEditDisabledForDescription;
  const procedureRunUIString = getProcedureRunUIStringForStudyType(props.studyType);
  const lowerMethodString = getProcedureUIString(ProceduresStringKeys.METHOD, procedureRun.procedureType, {
    stringCase: StringCase.LOWER,
  });
  const splitPoolEditDisabledTooltip = isEditDisabledForSplitPool
    ? `You can only edit the run design after every replicate in the previous ${lowerMethodString} is complete and before the next one starts`
    : undefined;
  const descriptionEditDisabledTooltip = isEditDisabledForDescription
    ? `Save or cancel ${procedureRunUIString} description changes before editing the ${procedureRunUIString} design`
    : undefined;
  const editDisabledTooltip = descriptionEditDisabledTooltip ?? splitPoolEditDisabledTooltip;

  // Block Save while any active-layer node lacks an incoming upstream edge, mirroring the backend's
  // reachability validation. Inert unless both the flowchart UI and split-pool are on (the legacy UI
  // cannot draw these edges).
  const isActiveLayerConnectedForSave =
    !isFlowchartProcedureRunDesignEnabled ||
    !isSplitPoolEnabled ||
    isActiveLayerConnected(
      conditions,
      templateFlowchart.nodes,
      allEdgeSpecs.map((spec) => ({source: spec.sourceNodeId, target: spec.targetNodeId})),
      activeMethod?.id ?? null
    );

  const procedureRunUpdateParam = useProcedureRunUpdateParam(
    procedureRun.id,
    conditions,
    experimentalInputs,
    context?.evaluatedValues,
    conditionReplicateFlowchart,
    methodExecutionInstanceIdsToRemove
  );

  // Shared run-flavored limit predicate/tooltip: drives the left-panel add buttons and the flowchart
  // add-replicate button so both disable at the same replicate limit with the same copy.
  const runButtonVariant = runConditionGroupsButtonVariant({
    conditions,
    methodExecutionLimitState,
    procedureType: procedureRun.procedureType,
  });
  const editNumColsButtons = (
    <EditNumConditionGroupsButtons
      conditions={conditions}
      setConditions={setConditions}
      methodExecutionLimitState={methodExecutionLimitState}
      procedureType={procedureRun.procedureType}
      showReplicateLimitLabel={true}
      {...runButtonVariant}
    />
  );
  const estimateResult = useTimingEstimate<{updates: Array<UpdateProcedureRunParams>}>(
    '/1/api/procedure-runs:estimate',
    // Don't want an estimate if there are no new conditions
    getNewConditionCount(procedureRunUpdateParam) > 0 ? {updates: [procedureRunUpdateParam]} : null
  );
  const newConditionReplicateCount = getNewConditionReplicateCount(procedureRunUpdateParam);
  const hasUnsavedNewConditions =
    editState === StudyDesignTabEditState.EDITING && getNewConditionCount(procedureRunUpdateParam) > 0;
  const newConditionsBannerText = hasUnsavedNewConditions
    ? `${NEW_WORKSHEETS_BANNER_TEXT} ${UNSAVED_NEW_CONDITIONS_BANNER_TEXT}`
    : NEW_WORKSHEETS_BANNER_TEXT;

  // Cleared when entering a new edit session (onEdit) so the banner returns next time.
  const [isEditBannerDismissed, setIsEditBannerDismissed] = useState(false);

  async function handleSave(): Promise<void> {
    updateEditState(StudyDesignTabEditState.SAVING);
    assert(conditions != null, 'Conditions should not be null for existing run');
    assert(experimentalInputs != null, 'Experimental inputs should not be null for existing run.');
    assert(context != null, 'Expected context to be non-null');

    const newConditionCount = getNewConditionCount(procedureRunUpdateParam);
    const hasNewConditions = newConditionCount > 0;
    const callbacks = {
      // Runs on success for both sync and async saves. Reset edit state here rather than via a
      // remount. Refreshing the design query caches the new edges as derived for a later remount.
      // Only the condition flow needs the details view.
      onSuccess: async () => {
        updateEditState(StudyDesignTabEditState.SAVED);
        resetEdits();
        const refreshedData = await refreshProcedureRunDesign(procedureRun.id);
        if (refreshedData != null && !isInaccessible(refreshedData.procedureRun)) {
          const refreshedFlowchart = getPowAgnosticTemplateFlowchartVersionFromProcedureRun(
            refreshedData.procedureRun
          );
          if (!isInaccessible(refreshedFlowchart)) {
            const refreshedDesignState = getStudyDesignFromProcedureRun(refreshedData.procedureRun, {
              requireBowlingLaneCoverage: !isSplitPoolEnabled,
            });
            resetConditionsAndInputs(
              refreshedDesignState.initialPersistedConditionGroups,
              refreshedDesignState.experimentalInputs
            );
          }
        }
        if (hasNewConditions || methodExecutionInstanceIdsToRemove.length > 0) {
          await refreshProcedureRunDetailsView(procedureRun.id);
        }
      },
      // Adding a condition opens the Overview tab to show the newly added conditions. Edges aren't
      // shown in the overview tab, so no navigation is needed.
      onOpen: () => {
        if (hasNewConditions) {
          onOpenAfterAddConditions(props.studyId, procedureRun.id);
          props.setCurrentProcedureRunTab(ProcedureRunTab.OVERVIEW);
        }
      },
      // Go back to editing state, so the user can try to re-submit
      onError: () => {
        updateEditState(StudyDesignTabEditState.EDITING);
      },
    };

    if (isSplitPoolEnabled) {
      await onUpdateProcedureRun(
        props.studyId,
        procedureRun.procedureType,
        props.studyType,
        // newConditionCoveredMethodIds is undefined when split-pool is disabled or when there's no active
        // method (in which case editing is disabled). In those cases, default to the number of unit ops in
        // the flowchart, although this number won't actually be used
        newConditionCoveredMethodIds?.length ?? templateFlowchart.nodes.length,
        procedureRunUpdateParam,
        callbacks
      );
      return;
    }

    await onAddConditions(
      props.studyId,
      procedureRun.procedureType,
      props.studyType,
      procedureRunUpdateParam,
      callbacks
    );
  }

  async function handleConfirmSave(): Promise<void> {
    setIsUpdateRunDesignModalOpen(false);
    await handleSave();
  }

  // TODO (BNCH-156220): Remove this header in flowchart UI and move buttons elsewhere
  const designHeader = !procedureRun.flowchartIsExpanded && (
    <div className={css(studyDesignTabStyles.header)}>
      <div className={css(studyDesignTabStyles.tabTitle)}>Design</div>
      <div className={css(studyDesignTabStyles.bannerAndButtons)}>
        {/* In flowchart UI (flag on), this banner moves onto the canvas topBanner below. */}
        {!isFlowchartProcedureRunDesignEnabled && editState === StudyDesignTabEditState.EDITING && (
          <InlineBanner bannerType={InlineBannerType.INFO} text={NEW_WORKSHEETS_BANNER_TEXT} />
        )}
        <StudyDesignTabSavingButtons
          onCancel={() => {
            updateEditState(StudyDesignTabEditState.SAVED);
            resetConditionsAndInputs(initialPersistedConditionGroups, initialExperimentalInputs);
            resetEdits();
            resetNodeRemovals();
          }}
          onEdit={() => {
            setIsEditBannerDismissed(false);
            updateEditState(StudyDesignTabEditState.EDITING);
          }}
          onSave={() => {
            setIsUpdateRunDesignModalOpen(true);
          }}
          editState={editState}
          isSaveDisabled={
            !areProcedureRunInputsEvaluatedValuesValid(context.evaluatedValues) ||
            !validateConditions(conditions) ||
            !isNumConditionReplicatesValid(methodExecutionLimitState) ||
            (conditions.length === initialPersistedConditionGroups.length &&
              drawnEdgeSpecs.length === 0 &&
              removedEdgeIds.size === 0 &&
              removedNodeIds.size === 0) ||
            !isActiveLayerConnectedForSave
          }
          saveDisabledTooltip={
            !isActiveLayerConnectedForSave
              ? `Every ${lowerMethodString} must be connected to the rest of the flowchart.`
              : undefined
          }
          isEditDisabled={isEditDisabled}
          editDisabledTooltip={editDisabledTooltip}
        />
      </div>
    </div>
  );

  if (isEmptyFlowchart) {
    return (
      <div className={css(studyDesignTabStyles.bufferTabWrapper)}>
        <InlineBanner
          bannerType={InlineBannerType.INFO}
          text={`No ${getProcedureUIString(ProceduresStringKeys.METHOD, procedureRun.procedureType, {
            stringCase: StringCase.LOWER,
            plural: true,
          })} have been configured for this ${getProcedureUIString(
            ProceduresStringKeys.PROCEDURE,
            procedureRun.procedureType,
            {stringCase: StringCase.LOWER}
          )}.`}
        />
      </div>
    );
  }

  // If the flowchart procedure run design is not enabled, we should have a selected method id
  if (!isFlowchartProcedureRunDesignEnabled) {
    assert(
      selectedMethodId != null,
      'Expected selected method id to be non-null for non-flowchart (legacy) procedure run design'
    );
  }

  return (
    <div
      className={css(studyDesignTabStyles.designBackground)}
      {...getTestProps({component: 'ProcedureRunDesignTabContents', element: 'root'})}
    >
      {isUpdateRunDesignModalOpen && (
        <UpdateRunDesignConfirmationModal
          conditionReplicateCount={newConditionReplicateCount}
          removedConditionReplicateCount={removedConditionReplicateCount}
          activeMethodName={activeMethod?.name ?? null}
          downstreamUnitOperationCount={downstreamUnitOperationCount}
          onRequestClose={() => {
            setIsUpdateRunDesignModalOpen(false);
          }}
          onRequestSubmit={handleConfirmSave}
        />
      )}
      {designHeader}
      {isFlowchartProcedureRunDesignEnabled ? (
        <ProcedureRunDesignFlowchartEditor
          conditions={conditions}
          methods={templateFlowchart.nodes}
          activeMethodId={activeMethod?.id ?? null}
          procedureType={procedureRun.procedureType}
          selectedMethodId={selectedMethodId}
          onChangeSelectedMethod={setSelectedMethodId}
          isInEditState={editState === StudyDesignTabEditState.EDITING}
          allowEditingPersistedConditions={false}
          statusByNodeId={statusByNodeId}
          edgeSpecs={allEdgeSpecs}
          onDrawEdge={addDrawnEdge}
          onRemoveEdge={removeEdge}
          showArchived={isSplitPoolEnabled && showArchived}
          onAddConditionReplicate={onAddConditionReplicate}
          isAddConditionReplicateDisabled={runButtonVariant.isAtLimit}
          addConditionReplicateDisabledTooltip={runButtonVariant.limitTooltipText}
          onRemoveDraftConditionReplicate={onRemoveDraftConditionReplicate}
          removedNodeIds={removedNodeIds}
          onRemovePersistedConditionReplicate={updateRemovedNodeIds}
          topBanner={
            editState === StudyDesignTabEditState.EDITING && !isEditBannerDismissed ? (
              <InlineBanner
                bannerType={InlineBannerType.INFO}
                text={newConditionsBannerText}
                onDismiss={() => {
                  setIsEditBannerDismissed(true);
                }}
              />
            ) : null
          }
          leftPanelChildren={
            <ProcedureRunDesignEditorLeftSidepanel
              isSplitPoolEnabled={isSplitPoolEnabled}
              activeMethodName={activeMethod?.name ?? null}
              doesActiveMethodHaveDownstreamMethods={doesActiveMethodHaveDownstreamMethods}
              showArchived={showArchived}
              onShowArchivedChange={setShowArchived}
              conditions={conditions}
              setConditions={setConditions}
              experimentalInputs={experimentalInputs}
              setExperimentalInputs={setExperimentalInputs}
              editState={editState}
              procedureType={procedureRun.procedureType}
              methodExecutionLimitState={methodExecutionLimitState}
              studyType={props.studyType}
              dataEntryMode={procedureRun.defaultDataEntryMode}
              isPlan={false}
              procedureId={procedureId}
              flowchartVersionId={getFlowchartVersionIdForNavigation(flowchartVersion)}
              newConditionCoveredMethodIds={newConditionCoveredMethodIds}
            />
          }
          // Render the Design panel contents only once a method is selected: the sidepanel requires a
          // non-null selectedMethodId, and the editor keeps the right panel closed without a selection.
          rightPanelChildren={
            selectedMethodId != null && (
              <ProcedureRunDesignEditorRightSidepanel
                templateFlowchart={templateFlowchart}
                selectedMethodId={selectedMethodId}
                procedureType={procedureRun.procedureType}
                context={context}
              />
            )
          }
        />
      ) : (
        <div className={css(studyDesignTabStyles.leftSidepanelContainer)}>
          <FlowchartSidebar
            icons={[
              {
                name: 'note-table',
                onClick: () => {
                  setSidepanelName(PARAMETER_VALUES_TAB_NAME);
                },
              },
              {
                name: 'settings',
                onClick: () => {
                  setSidepanelName(SETTINGS_TAB_NAME);
                },
              },
            ]}
            selectedIconIndex={selectedDesignTabIndex}
            setSelectedIconIndex={setSelectedDesignTabIndex}
          />
          <ContextPanel
            sidepanelName={sidepanelName}
            sidepanelHeaderContent={
              selectedDesignTabIndex === 0 && editState === StudyDesignTabEditState.EDITING
                ? editNumColsButtons
                : undefined
            }
          >
            {selectedDesignTabIndex === 0 ? (
              <ParameterValuesTabContents
                methods={templateFlowchart.nodes}
                procedureFlowchart={templateFlowchart}
                selectedMethodId={selectedMethodId!} // We assert this is non-null above
                onChangeSelectedMethod={setSelectedMethodId}
                procedureType={procedureRun.procedureType}
                context={context}
              />
            ) : (
              <SettingsTabContents
                conditions={conditions}
                setConditions={setConditions}
                experimentalInputs={experimentalInputs}
                setExperimentalInputs={setExperimentalInputs}
                locked={editState !== StudyDesignTabEditState.EDITING}
                procedureType={procedureRun.procedureType}
                methodExecutionLimitState={methodExecutionLimitState}
                studyType={props.studyType}
                dataEntryMode={procedureRun.defaultDataEntryMode}
                estimateResult={estimateResult}
                isPlan={false}
              />
            )}
          </ContextPanel>
        </div>
      )}
    </div>
  );
}

export default function QueryingProcedureRunDesignTabContents({
  procedureRunId,
  studyType,
  studyId,
  setCurrentProcedureRunTab,
}: {
  procedureRunId: string;
  studyType: StudyType;
  studyId: string;
  setCurrentProcedureRunTab: (tab: ProcedureRunTab) => void;
}): JSX.Element {
  const procedureRunUIString = getProcedureRunUIStringForStudyType(studyType);
  const procedureTemplateString = getProcedureTemplateStringForStudyType(studyType);
  const result = useBenchlingQuery<
    MultiProcedureRunStudyDesignTabContentsQuery,
    MultiProcedureRunStudyDesignTabContentsQueryVariables
  >(MULTI_PROCEDURE_RUN_STUDY_DESIGN_TAB_CONTENTS_QUERY, {variables: {procedureRunId}});

  if (result.type !== QueryResultType.SUCCESS) {
    return <CenteredHorizontalThrobber />;
  }

  const {data} = result;
  if (isInaccessible(data.procedureRun)) {
    return (
      <StudyInaccessibleContentBanner
        inaccessibleInfo={data.procedureRun}
        modelType={ModelTypeConstants.PROCEDURE_RUN}
        displayErrorText={`This ${procedureRunUIString} is no longer accessible. The ${procedureRunUIString} may have been moved into a project that you do not have access to.`}
      />
    );
  }
  const flowchartVersion = getPowAgnosticTemplateFlowchartVersionFromProcedureRun(data.procedureRun);
  if (isInaccessible(flowchartVersion)) {
    return (
      <StudyInaccessibleContentBanner
        inaccessibleInfo={flowchartVersion}
        modelType={ModelTypeConstants.PROCEDURE}
        displayErrorText={`The design of this ${procedureRunUIString} is not accessible. The ${procedureTemplateString} associated with this ${procedureRunUIString} may have been moved into a template collection that you do not have access to.`}
      />
    );
  }

  return (
    <ProcedureRunDesignTabContents
      procedureRun={data.procedureRun}
      studyType={studyType}
      studyId={studyId}
      setCurrentProcedureRunTab={setCurrentProcedureRunTab}
    />
  );
}
